import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import "./coupon.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { BrandInputWhite } from "../components/form/inputs";
import { BrandButton, BrandButtonWhite } from "../components/form/buttons";
import { Alert, Snackbar } from "@mui/material";
import { CourseFee } from "../components/utils";
import { updateUserPaidStatus } from "../components/utils";
import NetworkError from "../components/networkError";

interface PageResponse {
  success: boolean;
  coupon_amount?: string | number | undefined;
  message?: string;
}

interface PageProps {
  token: string | null;
  userid: number | null;
  userData: any;
  username: string
}

interface CouponResponse {
  data: {
    coupon_amount: number;
  };
}

const Coupon = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [searchingCoupon, setSearchingCoupon] = useState(false);
  const couponCode = useRef("");
  const location = useLocation();
  const [snackError, setSnackError] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const { token, userid, userData, username } = (location.state as PageProps) || {};
  const vertical = "bottom";
  const horizontal = "center";
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);

  const { isLoading: couponLoading, error: couponError } = useQuery(
    "getCouponData",
    async () => {
      const resp: CouponResponse = await axios.post(
        `${ApiEndpoint}wp-json/firstchoiceprep/v1/checkCoupon?coupon_code=${couponCode.current}`,
        { token, userid }
      );
      setSearchingCoupon(false);

      if(!resp) {
        setHasError(true);
        return;
      }

      if (resp.data.coupon_amount === CourseFee) {
        const paidResp = await updateUserPaidStatus(token, userid);

        if (paidResp) {
          navigate("/register-confirm", {
            replace: true,
          });
        } else {
          setPageLoading(false);
          setSnackError(true);
        }
      } else {
        setdata(resp.data as PageResponse);
      }
    },
    { enabled: searchingCoupon }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parseCouponData = () => {
    if (data && data.success) {
      let totalAmount = 0;
      if (data?.coupon_amount && typeof data?.coupon_amount === "number") {
        totalAmount = CourseFee - data.coupon_amount;
      }
      createPaymentIntent(totalAmount);
    } else {
      setPageLoading(false);
      setSnackError(true);
    }
  };

  const createPaymentIntent = async (amount: string | number | undefined) => {
    const result = await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/createPayment",
      {
        amount: amount,
        metadata: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          username: username,
        },
      }
    ); 
    setPageLoading(false);
    if (result) {
      navigate("/register-pay", {
        replace: true,
        state: {
          paymentIntentId: result.data.data.paymentIntentId,
          clientSecret: result.data.data.clientSecret,
          token,
          userid,
          chargeAmount: amount,
          userData,
          username
        },
      });
    } else {
      setSnackError(true);
    }
  };

  useEffect(() => {
    if (data) {
      parseCouponData();
    }
  }, [data, parseCouponData]);

  if (!token || !userid) return <LoadError />;
  if (couponError) setSnackError(true);

  if (couponLoading || pageLoading) return <Loader />;

  return (
    <div className="couponPage">
      <div className="headline">
        <h1>Thank You For Registering</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{
            __html: "You will now need to pay for your exam.",
          }}
        />
      </div>
      <div className="coupon-container">
        <p>If you were provided a coupon code for your exam, enter it here.</p>
        <div className="coupon-input-row">
          <BrandInputWhite
            onChange={(evt) => {
              couponCode.current = evt.target.value;
            }}
            id="filled-basic"
            label="Your Coupon Code"
            variant="filled"
            //   value={fillInValue}
          />
          {couponLoading ? (
            <BrandButton>loading...</BrandButton>
          ) : (
            <BrandButton
              onClick={() => {
                setSearchingCoupon(true);
                setPageLoading(true);
              }}
            >
              Go
            </BrandButton>
          )}
        </div>
      </div>
      <div className="no-coupon-container">
        <p>Don't have a coupon code?</p>
        <BrandButtonWhite
          onClick={() => createPaymentIntent(75)}
          variant="outlined"
        >
          CONTINUE
        </BrandButtonWhite>
      </div>

      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={() => setSnackError(false)}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">Sorry, No Coupon Found</Alert>
      </Snackbar>
      <NetworkError open={hasError} />

    </div>
  );
};

export default Coupon;
